import React from 'react';

const STORAGE_KEY = 'polyrth.skipLanding.v1';

export function shouldSkipLanding() {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function setSkipLanding(v) {
  try {
    localStorage.setItem(STORAGE_KEY, v ? '1' : '0');
  } catch {
    // ignore
  }
}

export default function Landing({ onEnter }) {
  return (
    <div className="min-h-screen w-full bg-black text-white overflow-x-hidden">
      {/* Background (space / moon arc) */}
      <div className="pointer-events-none fixed inset-0">
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(1200px 900px at 60% 30%, rgba(255,255,255,0.06), transparent 60%), radial-gradient(900px 700px at 20% 20%, rgba(0,16,255,0.10), transparent 55%), radial-gradient(1000px 800px at 50% 120%, rgba(255,255,255,0.05), transparent 60%)',
          }}
        />
        {/* Moon */}
        <div
          className="absolute -bottom-[45vh] right-[-20vw] h-[90vh] w-[90vh] rounded-full opacity-70"
          style={{
            background:
              'radial-gradient(circle at 35% 35%, #f5f5f5 0%, #d9d9d9 35%, #6b6b6b 62%, #1a1a1a 78%, rgba(0,0,0,0) 79%)',
            filter: 'grayscale(100%) contrast(105%)',
          }}
        />
        {/* Grain */}
        <div
          className="absolute inset-0 opacity-[0.14] mix-blend-overlay"
          style={{
            backgroundImage:
              'radial-gradient(rgba(255,255,255,0.10) 1px, transparent 1px), radial-gradient(rgba(255,255,255,0.07) 1px, transparent 1px)',
            backgroundSize: '3px 3px, 5px 5px',
            backgroundPosition: '0 0, 2px 1px',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/55 to-black/85" />
      </div>

      <div className="relative z-10 mx-auto max-w-6xl px-6 pt-24 pb-24">
        {/* Hero */}
        <div className="max-w-3xl">
          <div className="polyrth-mono text-xs tracking-[0.35em] uppercase text-white/60">
            polyrth
          </div>

          <h1 className="mt-5 polyrth-display polyrth-outline text-[42px] sm:text-[68px] leading-[0.95] font-black uppercase tracking-[0.08em]">
            Explore
            <br />
            your status.
          </h1>

          <div className="mt-6 polyrth-mono text-sm sm:text-base leading-relaxed text-white/65 max-w-xl">
            Enter a wallet address and check eligibility for the polyrth collection.
          </div>

          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={() => onEnter?.()}
              className="polyrth-mono rounded-xl border border-white/25 bg-white text-black hover:bg-white/90 px-6 py-4 text-sm font-bold tracking-[0.22em] uppercase"
            >
              Check status
            </button>
          </div>
        </div>

        {/* Spacer to make it scrollable */}
        <div className="h-20 sm:h-28" />
        

        {/* Collection section */}
        <div id="collection" className="max-w-4xl relative">
          <div className="absolute inset-0 bg-gradient-to-br from-transparent via-black/30 to-transparent opacity-30" />
          
          <div className="polyrth-mono text-xs tracking-[0.35em] uppercase text-white/45 relative">
            <span className="bg-gradient-to-r from-white/20 to-transparent bg-clip-text text-transparent">
              CØLLΞCTIØN
            </span>
          </div>

          <h2 className="mt-4 polyrth-display text-2xl sm:text-4xl font-black uppercase tracking-[0.12em] relative">
            <span className="bg-black text-transparent bg-clip-text">
              POLYRTH
            </span>
            <span className="absolute top-0 left-0 bg-gradient-to-r from-white/10 via-white/30 to-white/10 bg-clip-text text-transparent animate-pulse">
              POLYRTH
            </span>
          </h2>

          <div className="mt-4 relative">
            <div className="polyrth-mono text-sm sm:text-base leading-relaxed text-white/65">
              <span className="bg-black/40 px-1">A</span>{' '}
              <span className="bg-gradient-to-r from-black/40 to-black/60 px-1 relative">
                <span className="absolute inset-0 bg-black/30 animate-pulse" />
                limited
              </span>{' '}
              NFT collection built around{' '}
              <span className="relative">
                <span className="absolute -inset-1 bg-gradient-to-r from-transparent via-white/5 to-transparent" />
                <span className="bg-black/50 px-1">on-chain</span>
              </span>{' '}
              signal and{' '}
              <span className="bg-gradient-to-r from-black/40 via-black/60 to-black/40 px-1">
                <span className="opacity-70">provenance</span>
              </span>.
            </div>
            
            <div className="mt-2 polyrth-mono text-sm sm:text-base leading-relaxed text-white/40">
              <span className="bg-black/50 px-1">[REDACTED]</span>{' '}
              is phased. Use the checker to see your{' '}
              <span className="relative">
                <span className="absolute inset-0 bg-black/40 animate-pulse rounded" />
                <span className="px-1">current status</span>
              </span>.
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Card 1 - Redacted */}
            <div className="rounded-2xl border border-white/5 bg-gradient-to-br from-black/30 to-black/60 p-5 relative overflow-hidden">
              <div className="absolute top-2 right-2 text-[8px] text-white/20">✱</div>
              <div className="polyrth-mono text-xs tracking-[0.28em] uppercase text-white/50">
                <span className="bg-black/40 px-1">CLASSIFIED</span>
              </div>
              <div className="mt-2 polyrth-mono text-sm text-white/50">
                <div className="bg-black/30 h-3 w-3/4 mb-1 rounded"></div>
                <div className="bg-black/40 h-3 w-full rounded"></div>
              </div>
            </div>

            {/* Card 2 - Encrypted */}
            <div className="rounded-2xl border border-white/5 bg-gradient-to-br from-black/40 to-black/70 p-5 relative overflow-hidden">
              <div className="absolute top-2 right-2 text-[8px] text-white/20">✱</div>
              <div className="polyrth-mono text-xs tracking-[0.28em] uppercase text-white/45">
                <span className="bg-gradient-to-r from-transparent via-white/10 to-transparent px-1">
                  ENCRYPTED
                </span>
              </div>
              <div className="mt-2 polyrth-mono text-sm text-white/40">
                <div className="relative">
                  <span className="opacity-0">The list is </span>
                  <span className="absolute left-0 top-0 bg-black/50 px-1 animate-pulse">
                    [SECURE TRANSMISSION]
                  </span>
                </div>
              </div>
            </div>

            {/* Card 3 - Minimal with glitch */}
            <div className="rounded-2xl border border-white/5 bg-gradient-to-br from-black/50 to-black/80 p-5 relative overflow-hidden">
              <div className="absolute top-2 right-2 text-[8px] text-white/20">✱</div>
              <div className="polyrth-mono text-xs tracking-[0.28em] uppercase text-white/40">
                <span className="bg-black/60 px-1">MINIMAL</span>
              </div>
              <div className="mt-2 polyrth-mono text-sm text-white/35">
                One action:{' '}
                <span className="relative inline-block">
                  <span className="bg-black/70 px-1">[INPUT REQUIRED]</span>
                  <span className="absolute -bottom-0.5 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent"></span>
                </span>
                , get{' '}
                <span className="bg-gradient-to-r from-black/60 to-black/80 px-1">
                  [RESPONSE]
                </span>
                .
              </div>
            </div>
          </div>

          {/* Hidden message that appears on hover */}
          <div className="mt-10 relative group">
            <div className="polyrth-mono text-xs text-white/25 transition-opacity duration-500 group-hover:opacity-0">
              [DATA EXPUNGED] Server-side verification protocols active.
            </div>
            <div className="absolute inset-0 polyrth-mono text-xs text-white/45 opacity-0 transition-opacity duration-500 group-hover:opacity-100 pointer-events-none">
              <span className="bg-gradient-to-r from-white/10 to-transparent bg-clip-text text-transparent">
                Access requires authentication. Check your clearance level.
              </span>
            </div>
          </div>
        </div>

        <div className="mt-16 border-t border-white/5 pt-8 relative">
          <div className="polyrth-mono text-xs text-white/20">
            © {new Date().getFullYear()} [ENTITY: POLYRTH]. All rights reserved.
          </div>
          <div className="absolute bottom-0 right-0 text-[6px] text-white/10">
            v0x{Math.random().toString(16).slice(2, 8)}
          </div>
        </div>
      </div>
    </div>
  );
}

